import type { ComponentType } from 'preact'
import type { Store } from '../net/store'
import { useStore } from '../ui/useStore'
import { XpBar } from './LevelBadge'
import styles from './profile.module.css'
import type { RewardView } from './raceRewards'

/**
 * The last race's rewards (T2.4, D-053 slot `rewardLines`): +XP / +coins from the own result
 * row, then the saved reward's XP bar filling old → new and "Level up! Lv N"; "(not saved)"
 * when no reward came in time, "Progress is not saved offline" in offline mode; then one line
 * per daily task the race moved ("Use 8 items 5/8", "Task complete! +60 coins", T2.5).
 */
export function RewardLinesView({ view }: { view: RewardView }) {
  const { bar } = view
  return (
    <div class={styles.rewards} aria-live="polite" data-testid="reward-lines">
      <p class={styles.earned}>
        <span class={styles.plusXp}>+{view.xp} XP</span>
        <span class={styles.plusCoins}>+{view.coins} coins</span>
        {view.save === 'unsaved' && <span class={styles.note}>(not saved)</span>}
      </p>
      {view.save === 'offline' && <p class={styles.note}>Progress is not saved offline</p>}
      {bar && (
        <div class={styles.levelRow}>
          <span class={styles.badge}>Lv {bar.level}</span>
          <XpBar
            key={`${bar.level}:${bar.from}:${bar.to}`}
            from={bar.from}
            to={bar.to}
            label="Experience"
          />
          {/* In the bar's row, so a level-up adds no height (740×360 fits, T1.9). */}
          {view.levelUp !== null && <span class={styles.levelUp}>Level up! Lv {view.levelUp}</span>}
        </div>
      )}
      {view.tasks.length > 0 && (
        <ul class={styles.taskLines} data-testid="reward-tasks">
          {view.tasks.map((t) => (
            <li key={t.id} class={styles.taskLine}>
              <span>
                {t.text} {t.progress}/{t.target}
              </span>
              {t.completedNow && (
                <span class={styles.taskDone}>Task complete! +{t.coins} coins</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The no-props component the session exposes, bound to its rewards store. */
export function makeRewardLines(rewards: Store<RewardView | null>): ComponentType {
  return function RewardLines() {
    const view = useStore(rewards)
    return view ? <RewardLinesView view={view} /> : null
  }
}
